"use client";

import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";

type ToastTone = "error" | "success" | "info";

interface Toast {
  id: number;
  tone: ToastTone;
  message: string;
}

interface ToastApi {
  error: (message: string) => void;
  success: (message: string) => void;
  info: (message: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

/** How long a toast stays up. Errors linger — they usually carry something the
 *  user has to read and act on — where a success is just an acknowledgement. */
const DURATION: Record<ToastTone, number> = { error: 7000, success: 3500, info: 5000 };

/**
 * Transient feedback, replacing `window.alert()`.
 *
 * `alert()` was doing three things wrong at once: it blocks the whole page until
 * dismissed, it cannot be styled so it breaks out of the app visually, and on
 * mobile it is a system dialog that looks like the browser complaining rather
 * than the app responding. None of that suits "couldn't save that, try again".
 *
 * Deliberately small: no queueing library, no positions API, no promise
 * integration. Three tones and a timer is the whole surface, because every call
 * site here is reporting the outcome of one action.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const push = useCallback((tone: ToastTone, message: string) => {
    const id = nextId.current++;
    setToasts((list) => [...list, { id, tone, message }]);
    setTimeout(() => dismiss(id), DURATION[tone]);
  }, [dismiss]);

  const api = useMemo<ToastApi>(() => ({
    error: (m) => push("error", m),
    success: (m) => push("success", m),
    info: (m) => push("info", m),
  }), [push]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      {/* aria-live so a screen reader hears the outcome — the thing a blocking
          alert() got right for free and a styled div has to ask for. */}
      <div className="toast-viewport" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast toast--${t.tone}`}>
            <span className="toast-icon" aria-hidden="true">
              {t.tone === "error" ? (
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <circle cx="12" cy="12" r="9" /><path d="M12 7v6M12 16.5v.01" />
                </svg>
              ) : t.tone === "success" ? (
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12.5 10 17l9-10" />
                </svg>
              ) : (
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <circle cx="12" cy="12" r="9" /><path d="M12 11v5.5M12 7.5v.01" />
                </svg>
              )}
            </span>
            <span className="toast-message">{t.message}</span>
            <button type="button" className="toast-close" onClick={() => dismiss(t.id)} aria-label="Dismiss">
              ✕
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

/**
 * Feedback for the outcome of an action.
 *
 * Returns a no-op outside a provider rather than throwing: these calls sit in
 * catch blocks, and a missing provider must never turn "the save failed" into
 * "the component crashed".
 */
export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  return ctx ?? NOOP;
}

const NOOP: ToastApi = { error: () => {}, success: () => {}, info: () => {} };
